import React, { useEffect, useState } from 'react';
import toastr from 'toastr';
import { jobsAPI } from '../../utils/api';

/**
 * One place to move a stuck application: nudge the candidate about what they
 * still owe, set a reminder for yourself, or ping a teammate.
 */
const field = { width: '100%', padding: '0.5rem 0.625rem', border: '1px solid #D1D5DB', borderRadius: '6px', fontSize: '0.875rem', boxSizing: 'border-box', fontFamily: 'inherit' };
const label = { display: 'block', fontSize: '0.8125rem', color: '#374151', marginBottom: '0.25rem', fontWeight: 500 };
const tab = (active) => ({ padding: '0.5rem 0.875rem', border: 'none', borderBottom: active ? '2px solid #1E40AF' : '2px solid transparent', background: 'none', cursor: 'pointer', fontSize: '0.875rem', fontWeight: active ? 600 : 500, color: active ? '#1E40AF' : '#6B7280' });

function NudgeModal({ jobId, application, onClose, onDone }) {
  const [mode, setMode] = useState('candidate');
  const [pending, setPending] = useState(null);
  const [team, setTeam] = useState([]);
  const [dueAt, setDueAt] = useState('');
  const [note, setNote] = useState('');
  const [toUserId, setToUserId] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    jobsAPI.getApplicationPending(jobId, application._id).then(r => setPending(r.data)).catch(() => setPending({ pending: [], emailVerified: application.emailVerified }));
    jobsAPI.getTeam().then(r => setTeam(r.data.team || [])).catch(() => setTeam([]));
  }, [jobId, application._id, application.emailVerified]);

  const name = `${application.candidateInfo?.firstName || ''} ${application.candidateInfo?.lastName || ''}`.trim() || 'the candidate';
  const nudgedRecently = pending?.lastManualNudgeAt && Date.now() - new Date(pending.lastManualNudgeAt).getTime() < 24 * 3600 * 1000;

  const submit = async () => {
    setBusy(true);
    try {
      if (mode === 'candidate') {
        const r = await jobsAPI.nudgeApplication(jobId, application._id, { action: 'candidate' });
        toastr.success(`Sent to ${name}: ${(r.data.sent || []).join(', ')}`);
      } else if (mode === 'remind_me') {
        await jobsAPI.nudgeApplication(jobId, application._id, { action: 'remind_me', dueAt, note });
        toastr.success(`You will be reminded on ${new Date(dueAt).toLocaleDateString()}`);
      } else {
        const r = await jobsAPI.nudgeApplication(jobId, application._id, { action: 'ping', toUserId, note });
        toastr.success(`Sent to ${r.data.to}`);
      }
      onDone && onDone();
      onClose();
    } catch (err) {
      toastr.error(err.response?.data?.error || 'Could not send');
    } finally {
      setBusy(false);
    }
  };

  const canSubmit = mode === 'candidate' ? Boolean(pending?.pending?.length) && pending?.emailVerified && !nudgedRecently
    : mode === 'remind_me' ? Boolean(dueAt)
    : Boolean(toUserId);

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(17,24,39,0.45)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={onClose}>
      <div style={{ background: '#fff', borderRadius: '10px', width: 'min(520px, 92vw)', padding: '1.25rem 1.5rem 1.5rem', boxShadow: '0 20px 40px rgba(0,0,0,0.2)' }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '0.75rem' }}>
          <h3 style={{ margin: 0, fontSize: '1.0625rem', color: '#111827' }}>Move things along: {name}</h3>
          <button onClick={onClose} aria-label="Close" style={{ border: 'none', background: 'none', fontSize: '1.25rem', cursor: 'pointer', color: '#6B7280' }}>×</button>
        </div>
        <div style={{ display: 'flex', gap: '0.25rem', borderBottom: '1px solid #E5E7EB', marginBottom: '1rem' }}>
          <button style={tab(mode === 'candidate')} onClick={() => setMode('candidate')}>Nudge candidate</button>
          <button style={tab(mode === 'remind_me')} onClick={() => setMode('remind_me')}>Remind me</button>
          <button style={tab(mode === 'ping')} onClick={() => setMode('ping')}>Ping a teammate</button>
        </div>

        {mode === 'candidate' && (
          <div style={{ fontSize: '0.875rem', color: '#374151' }}>
            {pending === null && <p>Checking what is pending...</p>}
            {pending && pending.pending.length === 0 && <p>Nothing is pending on the candidate's side. Use "Remind me" or "Ping a teammate" if the next step is ours.</p>}
            {pending && pending.pending.length > 0 && (
              <>
                <p style={{ margin: '0 0 0.5rem' }}>One email, listing what we are still waiting for:</p>
                <ul style={{ margin: '0 0 0.75rem 1.25rem', padding: 0 }}>{pending.pending.map(p => <li key={p.key}>{p.label}</li>)}</ul>
                {!pending.emailVerified && <p style={{ color: '#991B1B' }}>The candidate has not verified their email, so we do not email them.</p>}
                {nudgedRecently && <p style={{ color: '#92400E' }}>Already nudged in the last 24 hours.</p>}
              </>
            )}
          </div>
        )}

        {mode === 'remind_me' && (
          <div style={{ display: 'grid', gap: '0.75rem' }}>
            <div><label style={label}>Remind me on</label><input type="date" style={field} value={dueAt} min={new Date(Date.now() + 86400000).toISOString().slice(0, 10)} onChange={(e) => setDueAt(e.target.value)} /></div>
            <div><label style={label}>Note to self (optional)</label><textarea rows={3} style={field} value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Schedule the screening call if the questionnaire is in" /></div>
          </div>
        )}

        {mode === 'ping' && (
          <div style={{ display: 'grid', gap: '0.75rem' }}>
            <div><label style={label}>Teammate</label>
              <select style={field} value={toUserId} onChange={(e) => setToUserId(e.target.value)}>
                <option value="">Choose a person</option>
                {team.map(t => <option key={t.id} value={t.id}>{t.name} ({t.role})</option>)}
              </select>
            </div>
            <div><label style={label}>What do you need from them?</label><textarea rows={3} style={field} value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Can you take the technical screening this week?" /></div>
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1.25rem' }}>
          <button onClick={onClose} style={{ padding: '0.5rem 1rem', background: '#F3F4F6', color: '#374151', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.875rem' }}>Cancel</button>
          <button onClick={submit} disabled={!canSubmit || busy} style={{ padding: '0.5rem 1.125rem', background: !canSubmit || busy ? '#D1D5DB' : '#1E40AF', color: '#fff', border: 'none', borderRadius: '6px', cursor: !canSubmit || busy ? 'not-allowed' : 'pointer', fontSize: '0.875rem', fontWeight: 600 }}>
            {busy ? 'Sending...' : mode === 'candidate' ? 'Send nudge' : mode === 'remind_me' ? 'Set reminder' : 'Send ping'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default NudgeModal;
